/**
 * NodeSearch - A search component for finding and navigating to nodes in React Flow
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { useReactFlow } from '@xyflow/react'
import styled from 'styled-components'
import {
  Search,
  X,
  Building2,
  Compass,
  Lightbulb,
  Info,
  Layers,
  Film,
  ScanSearch,
} from 'lucide-react'
import { BORDER_RADIUS, SPACING, TRANSITIONS, TYPOGRAPHY, FONT_WEIGHT } from '../../../styles/theme'
import { CATEGORY_STYLES } from '../../../constants/mindmapConfig'
import type {
  ReactFlowNodeData,
  VentureNodeData,
  SummarizedNodeData,
  CollapsedHierarchyNode,
} from '../../../types/mindmap-components'
import type { Node } from '@xyflow/react'
import type { NodeCategory } from '@bandos/shared'

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const SearchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

const SearchInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.xs};
  background: var(--color-bg-elevated);
  padding: 0 ${SPACING.md};
  height: 40px;
  border-radius: ${BORDER_RADIUS.lg};
  box-shadow: var(--shadow-dropdown);
  border: 1px solid var(--color-border-dark);
  width: 240px;
`

const SearchIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--color-text-secondary);
`

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  color: var(--color-text-primary);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  outline: none;
  min-width: 0;

  &::placeholder {
    color: var(--color-text-tertiary);
  }
`

const ClearButton = styled.button<{ $isVisible: boolean }>`
  width: 20px;
  height: 20px;
  border: none;
  background: var(--color-bg-hover);
  border-radius: ${BORDER_RADIUS.sm};
  color: var(--color-text-secondary);
  cursor: pointer;
  display: ${(props) => (props.$isVisible ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: ${TRANSITIONS.fast};

  &:hover {
    background: var(--color-border);
    color: var(--color-text-primary);
  }
`

const ResultsDropdown = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 320px;
  max-height: 400px;
  overflow-y: auto;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border-dark);
  border-radius: ${BORDER_RADIUS.lg};
  box-shadow: var(--shadow-dropdown);
  z-index: 1000;
  display: ${(props) => (props.$isVisible ? 'block' : 'none')};
`

const ResultsHeader = styled.div`
  padding: ${SPACING.sm} ${SPACING.md};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid var(--color-border);
`

const ResultItem = styled.button<{ $isHighlighted: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${SPACING.sm};
  width: 100%;
  padding: ${SPACING.sm} ${SPACING.md};
  border: none;
  background: ${(props) => (props.$isHighlighted ? 'var(--color-bg-hover)' : 'transparent')};
  cursor: pointer;
  text-align: left;
  transition: background 0.1s ease;

  &:hover {
    background: var(--color-bg-hover);
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--color-border);
  }
`

const ResultIcon = styled.div<{ $color: string }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--color-bg-card);
  border: 2px solid ${(props) => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const ResultContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const ResultLabel = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const ResultCategory = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  text-transform: capitalize;
`

const ResultMatch = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-tertiary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const NoResults = styled.div`
  padding: ${SPACING.lg};
  text-align: center;
  color: var(--color-text-tertiary);
  font-size: ${TYPOGRAPHY.caption.fontSize};
`

const KeyboardHint = styled.div`
  padding: ${SPACING.xs} ${SPACING.md};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-tertiary);
  border-top: 1px solid var(--color-border);
  display: flex;
  gap: ${SPACING.md};
  justify-content: center;
`

const KeyHint = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`

const Key = styled.kbd`
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: 3px;
  padding: 1px 4px;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-family: inherit;
`

// ============================================================================
// HELPERS
// ============================================================================

const CATEGORY_ICONS: Record<NodeCategory, typeof Search> = {
  venture: Building2,
  need: Compass,
  idea: Lightbulb,
  information: Info,
  summarized: Layers,
  storyboard: Film,
  insight: ScanSearch,
}

const CATEGORY_LABELS: Record<NodeCategory, string> = {
  venture: 'Venture',
  need: 'Unmet need',
  idea: 'Idea',
  information: 'Information',
  summarized: 'Collapsed',
  storyboard: 'Storyboard',
  insight: 'Insight',
}

interface SearchResult {
  node: Node<ReactFlowNodeData>
  matchedField: string
  matchedValue: string
}

/**
 * Get all searchable text from a node's data
 */
function getSearchableFields(data: ReactFlowNodeData): Array<{ field: string; value: string }> {
  const fields: Array<{ field: string; value: string }> = []

  // Common field - label
  if (data.label) {
    fields.push({ field: 'label', value: data.label })
  }

  // Venture node fields
  if (data.category === 'venture') {
    const ventureData = data as VentureNodeData
    if (ventureData.companyName) fields.push({ field: 'name', value: ventureData.companyName })
    if (ventureData.businessModelAndValueProp)
      fields.push({ field: 'vision', value: ventureData.businessModelAndValueProp })
    if (ventureData.capabilitiesAndAssets)
      fields.push({ field: 'strengths', value: ventureData.capabilitiesAndAssets })
    if (ventureData.hardConstraints)
      fields.push({ field: 'boundaries', value: ventureData.hardConstraints })
  }

  // Summarized node - search within collapsed hierarchy
  if (data.category === 'summarized') {
    const summarizedData = data as SummarizedNodeData
    const searchHierarchy = (node: CollapsedHierarchyNode) => {
      if (node.label) fields.push({ field: 'collapsed', value: node.label })
      node.children.forEach(searchHierarchy)
    }
    searchHierarchy(summarizedData.collapsedHierarchy)
  }

  return fields
}

/**
 * Search nodes for matching text
 */
function searchNodes(nodes: Node<ReactFlowNodeData>[], query: string): SearchResult[] {
  if (!query.trim()) return []

  const lowerQuery = query.toLowerCase()
  const results: SearchResult[] = []

  for (const node of nodes) {
    const fields = getSearchableFields(node.data)

    for (const { field, value } of fields) {
      if (value.toLowerCase().includes(lowerQuery)) {
        results.push({
          node,
          matchedField: field,
          matchedValue: value,
        })
        break // Only one result per node
      }
    }
  }

  // Sort by relevance - exact matches first, then by label
  return results.sort((a, b) => {
    const aExact = a.matchedValue.toLowerCase() === lowerQuery
    const bExact = b.matchedValue.toLowerCase() === lowerQuery
    if (aExact && !bExact) return -1
    if (!aExact && bExact) return 1
    return (a.node.data.label || '').localeCompare(b.node.data.label || '')
  })
}

// ============================================================================
// COMPONENT
// ============================================================================

export function NodeSearch() {
  const { getNodes, fitView, setNodes } = useReactFlow()
  const [query, setQuery] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const nodes = getNodes() as Node<ReactFlowNodeData>[]
  const results = useMemo(() => searchNodes(nodes, query), [nodes, query])

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Element)) {
        setIsFocused(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Reset highlighted index when results change
  useEffect(() => {
    setHighlightedIndex(0)
  }, [results.length])

  const handleClear = useCallback(() => {
    setQuery('')
    inputRef.current?.focus()
  }, [])

  const handleSelectNode = useCallback(
    (result: SearchResult) => {
      // Select the node
      setNodes((nodes) =>
        nodes.map((n) => ({
          ...n,
          selected: n.id === result.node.id,
        }))
      )

      // Fit view to the selected node
      setTimeout(() => {
        void fitView({
          nodes: [{ id: result.node.id }],
          duration: 500,
          padding: 0.5,
          maxZoom: 1,
        })
      }, 50)

      // Clear search (dropdown hides because query is empty)
      // Keep isFocused true so user can immediately search again
      setQuery('')
    },
    [setNodes, fitView]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setQuery('')
        setIsFocused(false)
        inputRef.current?.blur()
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightedIndex((i) => Math.min(i + 1, results.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightedIndex((i) => Math.max(i - 1, 0))
      } else if (e.key === 'Enter' && results[highlightedIndex]) {
        e.preventDefault()
        handleSelectNode(results[highlightedIndex])
      }
    },
    [results, highlightedIndex, handleSelectNode]
  )

  const showResults = isFocused && query.length > 0

  return (
    <SearchContainer ref={containerRef}>
      <SearchInputWrapper>
        <SearchIcon>
          <Search size={16} />
        </SearchIcon>

        <SearchInput
          ref={inputRef}
          type="text"
          placeholder="Search nodes..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
        />

        <ClearButton $isVisible={query.length > 0} onClick={handleClear} aria-label="Clear search">
          <X size={12} />
        </ClearButton>
      </SearchInputWrapper>

      <ResultsDropdown $isVisible={showResults}>
        <ResultsHeader>
          {results.length} result{results.length !== 1 ? 's' : ''}
        </ResultsHeader>

        {results.length > 0 ? (
          <>
            {results.slice(0, 20).map((result, index) => {
              const Icon = CATEGORY_ICONS[result.node.data.category]
              const color = CATEGORY_STYLES[result.node.data.category].iconColor
              const categoryLabel = CATEGORY_LABELS[result.node.data.category]

              return (
                <ResultItem
                  key={result.node.id}
                  $isHighlighted={index === highlightedIndex}
                  onClick={() => handleSelectNode(result)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <ResultIcon $color={color}>
                    <Icon size={14} color={color} />
                  </ResultIcon>
                  <ResultContent>
                    <ResultLabel>{result.node.data.label || 'Untitled'}</ResultLabel>
                    <ResultCategory>{categoryLabel}</ResultCategory>
                    {result.matchedField !== 'label' && (
                      <ResultMatch>
                        Matched in {result.matchedField}: "{result.matchedValue.slice(0, 50)}..."
                      </ResultMatch>
                    )}
                  </ResultContent>
                </ResultItem>
              )
            })}
            <KeyboardHint>
              <KeyHint>
                <Key>↑↓</Key> Navigate
              </KeyHint>
              <KeyHint>
                <Key>Enter</Key> Select
              </KeyHint>
              <KeyHint>
                <Key>Esc</Key> Close
              </KeyHint>
            </KeyboardHint>
          </>
        ) : (
          <NoResults>No nodes found matching "{query}"</NoResults>
        )}
      </ResultsDropdown>
    </SearchContainer>
  )
}
